/**
 * Guest list public URLs use the **current site origin** (localhost in dev, production host in prod).
 * Path shape: /guest/{handle}/{guestListSlug}[/optionalShowSegment]
 */

/** Always use the app’s origin so links match where you’re viewing (local vs production). */
export function getPublicSiteOrigin(): string {
  if (typeof window !== 'undefined') return window.location.origin
  return ''
}

function unwrapApiPayload(res: unknown): Record<string, unknown> | null {
  if (!res || typeof res !== 'object') return null
  const r = res as Record<string, unknown>
  const d = r.data
  if (d && typeof d === 'object' && !Array.isArray(d)) return d as Record<string, unknown>
  return r
}

function pickStr(obj: Record<string, unknown> | null | undefined, keys: string[]): string {
  if (!obj) return ''
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

export function slugifySegment(s: string): string {
  return (
    s
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64) || 'artist'
  )
}

/**
 * Prefer explicit handle from guestlist/me, then profile display name (same fields as Settings).
 * Falls back to localStorage-derived handle.
 */
export function resolveGuestListHandleFromApi(
  meRes: unknown,
  profileRes: unknown,
): string | null {
  const prof = unwrapApiPayload(profileRes)
  const user = prof && typeof prof.user === 'object' && prof.user ? (prof.user as Record<string, unknown>) : prof

  /** Prefer Settings / profile display name so the path matches what the user sees (not a stale stored handle). */
  const fromProfile =
    pickStr(prof, ['dizeeArtistName', 'artistName', 'artist_name']) ||
    pickStr(user || {}, ['dizeeArtistName', 'artistName', 'artist_name', 'name'])

  if (fromProfile) return slugifySegment(fromProfile)

  const me = unwrapApiPayload(meRes)
  const fromMe =
    pickStr(me, ['guestListPathHandle', 'guestListHandle', 'artistPathHandle', 'artistHandle']) ||
    pickStr(me, ['displayName', 'display_name', 'artistName', 'artist_name', 'name'])

  if (fromMe) {
    const t = fromMe.trim()
    if (/^[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(t)) return t.toLowerCase()
    return slugifySegment(t)
  }

  return getGuestListHandleFromStorage()
}

/** Derive a URL-safe handle from local user/artist (fallback only). */
export function getGuestListHandleFromStorage(): string | null {
  try {
    const raw = localStorage.getItem('dizeeUser')
    if (!raw) return null
    const u = JSON.parse(raw) as Record<string, unknown>
    const direct =
      (typeof u.guestListHandle === 'string' && u.guestListHandle) ||
      (typeof u.username === 'string' && u.username) ||
      (typeof u.artistSlug === 'string' && u.artistSlug)
    if (direct) return slugifySegment(String(direct))
    const artists = u.selectedArtists as { name?: string; slug?: string }[] | undefined
    const name = artists?.[0]?.name || (typeof u.name === 'string' ? u.name : '')
    const fromName = name ? slugifySegment(name) : ''
    return fromName || null
  } catch {
    return null
  }
}

/** Prefix before the list slug (main guest list URL is `/guest/{guestListSlug}` — one segment, no duplicate artist name). */
export function getGuestListUrlPrefix(): string {
  const origin = getPublicSiteOrigin()
  return `${origin}/guest/`
}

/** Main guest list page URL: `/guest/{guestListSlug}` (list id from API — not repeated with profile handle). */
export function buildGuestListPublicUrl(guestListSlug: string, _legacyHandleIgnored?: string | null): string {
  const origin = getPublicSiteOrigin()
  if (guestListSlug) {
    return `${origin}/guest/${encodeURIComponent(guestListSlug)}`
  }
  return `${origin}/l/guestlist/`
}

/**
 * Per-show public link: `/guest/{guestListSlug}/{showSlug}`.
 * `publicShowSlug` is stored on guestlist booking settings.
 */
export function buildGuestListShowPublicUrl(
  guestListSlug: string,
  _legacyHandleIgnored: string | null | undefined,
  showSlugSegment?: string | null,
): string {
  const base = buildGuestListPublicUrl(guestListSlug)
  const seg = (showSlugSegment || '').trim()
  if (!seg) return base
  const origin = getPublicSiteOrigin()
  const safe = slugifySegment(seg)
  return `${origin}/guest/${encodeURIComponent(guestListSlug)}/${encodeURIComponent(safe)}`
}

/**
 * Default URL segment when none is saved yet — matches how shows are labeled (event title first, then venue + date).
 * Uses event title when present so it lines up with calendar / list naming.
 */
export function suggestDefaultShowSlugFromBooking(
  venue: string,
  perfDateIso: string,
  eventTitle?: string | null,
): string {
  const baseLabel = (eventTitle && String(eventTitle).trim()) || venue || 'show'
  const v = slugifySegment(baseLabel)
  try {
    const d = new Date(perfDateIso)
    if (!Number.isNaN(d.getTime()) && perfDateIso && perfDateIso !== 'TBD') {
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return slugifySegment(`${baseLabel}-${mm}-${dd}`)
    }
  } catch {
    /* ignore */
  }
  return v || 'show'
}
